import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CalendarDays, ExternalLink, Image as ImageIcon, RefreshCw, Wand2 } from "lucide-react";
import { apiErrorMessage } from "@/lib/api";
import { useBrands } from "@/lib/queries";
import {
  useCalendar,
  useGeneratePost,
  useRegeneratePost,
  type CalendarPost,
} from "@/lib/content";
import { GenerateImageButton } from "@/components/GenerateImageButton";
import { ErrorBox, Field } from "./LoginPage";
import {
  CalendarFilter,
  boundsFor,
  type CalendarRange,
} from "@/components/CalendarFilter";

const STATUS_TONE: Record<string, string> = {
  draft: "bg-neutral-100 text-neutral-600",
  pending_approval: "bg-warning/10 text-warning",
  approved: "bg-primary-50 text-primary-700",
  scheduled: "bg-primary-50 text-primary-700",
  published: "bg-success/10 text-success",
  partially_failed: "bg-warning/10 text-warning",
  failed: "bg-danger/10 text-danger",
  skipped: "bg-neutral-100 text-neutral-500",
};

/**
 * Content calendar (spec §8).
 *
 * Times render in the *brand's* timezone, not the viewer's — an owner in
 * London scheduling for an Accra shop needs to see Accra time, otherwise
 * "8am" silently means something else.
 */
export function CalendarPage() {
  const { t } = useTranslation();
  const brands = useBrands();
  const [brandId, setBrandId] = useState<string>("");
  /**
   * Starts on "upcoming" rather than the old hidden default of this month.
   *
   * The thing someone opens a calendar to see is what is going out next, and
   * the previous behaviour — current month only, with nothing saying so —
   * meant anything scheduled for a later month was simply absent.
   */
  const [range, setRange] = useState<CalendarRange>({ key: "upcoming" });
  const [query, setQuery] = useState("");
  const calendar = useCalendar(brandId || null, boundsFor(range));
  const generate = useGeneratePost();
  const regenerate = useRegeneratePost();

  const [topic, setTopic] = useState("");
  const [withImage, setWithImage] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const targetBrand = brandId || brands.data?.[0]?.id || "";

  /**
   * Three lists out of one, because they are three different jobs: something
   * that went wrong needs fixing, something upcoming might need changing, and
   * something published is only there to be looked back at.
   *
   * Searching covers the topic and the captions. The topic is what a campaign
   * names a post, which is rarely what the owner remembers — they remember a
   * phrase they read.
   */
  const groups = useMemo(() => {
    const scheduled = calendar.data?.scheduled ?? [];
    const needle = query.trim().toLowerCase();

    const matches = (post: CalendarPost) => {
      if (!needle) return true;
      if (post.topic?.toLowerCase().includes(needle)) return true;
      return post.variants.some((v) => v.caption?.toLowerCase().includes(needle));
    };

    const visible = scheduled.filter(matches);
    const attention = visible.filter((p) => p.status === "failed" || p.status === "partially_failed");
    const published = visible.filter((p) => p.status === "published");
    const upcoming = visible.filter(
      (p) => p.status !== "published" && p.status !== "failed" && p.status !== "partially_failed",
    );

    return {
      attention,
      // Most recent first: looking back, the last thing posted is the thing
      // being looked for. Upcoming keeps its ascending order — the next one
      // out is the one that matters.
      published: [...published].reverse(),
      upcoming,
      drafts: (calendar.data?.unscheduled ?? []).filter(matches),
      showing: visible.length,
      total: scheduled.length,
    };
  }, [calendar.data, query]);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setError(null);
    setNotice(null);
    if (!targetBrand) return;
    try {
      const result = await generate.mutateAsync({ brandId: targetBrand, topic, withImage });
      setTopic("");
      // A caption-only draft is still useful, so a failed image is a notice
      // rather than an error that hides the post that was created.
      if (result.imageError) setNotice(t("calendar.imageFailed"));
    } catch (err) {
      setError(apiErrorMessage(err, t("errors.generic")));
    }
  }

  return (
    <div className="space-y-6">
      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-ink">{t("calendar.title")}</h1>
          <p className="mt-1 text-sm text-muted">{t("calendar.subtitle")}</p>
        </div>
        <select
          value={brandId}
          onChange={(e) => setBrandId(e.target.value)}
          className="select"
        >
          <option value="">{t("calendar.allBrands")}</option>
          {(brands.data ?? []).map((b) => (
            <option key={b.id} value={b.id}>
              {b.name}
            </option>
          ))}
        </select>
      </header>

      {/* ── Generate ── */}
      <form
        onSubmit={submit}
        className="space-y-3 rounded-xl border border-border bg-white p-5 shadow-sm"
      >
        <h2 className="flex items-center gap-2 text-sm font-semibold text-ink">
          <Wand2 size={16} className="text-primary-600" /> {t("calendar.generateTitle")}
        </h2>
        <Field
          label={t("calendar.topic")}
          value={topic}
          onChange={setTopic}
          hint={t("calendar.topicHint")}
          required
        />
        <label className="flex items-center gap-2 text-sm">
          <input
            type="checkbox"
            checked={withImage}
            onChange={(e) => setWithImage(e.target.checked)}
            className="h-4 w-4 rounded border-border text-primary-600"
          />
          {t("calendar.withImage")}
        </label>
        {error ? <ErrorBox>{error}</ErrorBox> : null}
        {notice ? (
          <p className="rounded-lg border border-warning/30 bg-warning/10 px-3 py-2 text-xs text-warning">
            {notice}
          </p>
        ) : null}
        <button
          type="submit"
          disabled={generate.isPending || !targetBrand}
          className="btn-primary"
        >
          {generate.isPending ? t("calendar.generating") : t("calendar.generate")}
        </button>
      </form>

      <CalendarFilter
        range={range}
        onRange={setRange}
        query={query}
        onQuery={setQuery}
        counts={{ showing: groups.showing, total: groups.total }}
      />

      {calendar.data?.truncated ? (
        <p className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-sm text-warning">
          {t("calendar.truncated", { count: calendar.data.limit })}
        </p>
      ) : null}

      {calendar.isLoading ? (
        <p className="text-sm text-muted">{t("errors.loading")}</p>
      ) : (
        <>
          {/* Problems first: they are the only ones that need a decision. */}
          {groups.attention.length ? (
            <PostList
              title={t("calendar.needsAttention")}
              posts={groups.attention}
              emptyText=""
              onRegenerate={(id) => regenerate.mutate(id)}
              regenerating={regenerate.isPending}
            />
          ) : null}
          <PostList
            title={t("calendar.unscheduled")}
            posts={groups.drafts}
            emptyText={t("calendar.noDrafts")}
            onRegenerate={(id) => regenerate.mutate(id)}
            regenerating={regenerate.isPending}
          />
          <PostList
            title={t("calendar.upcoming")}
            posts={groups.upcoming}
            emptyText={t("calendar.noUpcoming")}
            onRegenerate={(id) => regenerate.mutate(id)}
            regenerating={regenerate.isPending}
          />
          <PostList
            title={t("calendar.published")}
            posts={groups.published}
            emptyText={t("calendar.noPublished")}
            onRegenerate={(id) => regenerate.mutate(id)}
            regenerating={regenerate.isPending}
          />
        </>
      )}
    </div>
  );
}

function PostList({
  title,
  posts,
  emptyText,
  onRegenerate,
  regenerating,
}: {
  title: string;
  posts: CalendarPost[];
  emptyText: string;
  onRegenerate: (id: string) => void;
  regenerating: boolean;
}) {
  const { t } = useTranslation();
  return (
    <section>
      <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold text-ink">
        <CalendarDays size={16} className="text-primary-600" /> {title}
        <span className="text-xs font-normal text-muted">({posts.length})</span>
      </h2>

      {posts.length === 0 ? (
        <p className="rounded-xl border border-dashed border-border bg-white/50 p-6 text-center text-sm text-muted">
          {emptyText}
        </p>
      ) : (
        <ul className="space-y-3">
          {posts.map((post) => (
            <li key={post.id} className="card-pad">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="truncate text-sm font-medium">
                    {post.topic ?? t("calendar.untitled")}
                  </p>
                  <p className="mt-0.5 text-xs text-muted">
                    {post.brand.name} ·{" "}
                    {post.scheduledAt
                      ? formatInZone(post.scheduledAt, post.brand.timezone)
                      : t("calendar.notScheduled")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span
                    className={`rounded-full px-2 py-0.5 text-[10px] font-semibold ${
                      STATUS_TONE[post.status] ?? "bg-neutral-100 text-neutral-600"
                    }`}
                  >
                    {t(`calendar.status.${post.status}`, { defaultValue: post.status })}
                  </span>
                  {post.status !== "published" ? (
                    <button
                      onClick={() => onRegenerate(post.id)}
                      disabled={regenerating}
                      title={t("calendar.regenerate")}
                      aria-label={t("calendar.regenerate")}
                      className="text-muted hover:text-ink disabled:opacity-50"
                    >
                      <RefreshCw size={14} />
                    </button>
                  ) : null}
                </div>
              </div>

              {post.mediaAsset ? (
                <div className="mt-3 flex items-center gap-2 text-xs text-muted">
                  <ImageIcon size={13} />
                  <a
                    href={post.mediaAsset.enhancedUrl ?? post.mediaAsset.originalUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="truncate text-primary-700 hover:underline"
                  >
                    {t("calendar.viewImage")}
                  </a>
                </div>
              ) : post.status !== "published" ? (
                /* No picture, and still time to add one. A published post's
                   platforms already have their copy, so an image added here
                   would change the dashboard and nothing a reader sees. */
                <GenerateImageButton postId={post.id} />
              ) : null}

              <ul className="mt-3 space-y-2 border-t border-border pt-3">
                {post.variants.map((v) => (
                  <li key={v.id}>
                    <p className="text-[11px] font-semibold uppercase tracking-wide text-muted">
                      {v.socialAccount.platform} · {v.socialAccount.displayName}
                    </p>
                    <p className="mt-0.5 whitespace-pre-wrap break-words text-sm">{v.caption}</p>
                    {v.hashtags.length > 0 ? (
                      <p className="mt-1 text-xs text-primary-700">
                        {v.hashtags.map((h) => (h.startsWith("#") ? h : `#${h}`)).join(" ")}
                      </p>
                    ) : null}
                    {v.errorMessage ? (
                      <p className="mt-1 text-xs text-danger">{v.errorMessage}</p>
                    ) : null}
                    {v.permalink ? (
                      <a
                        href={v.permalink}
                        target="_blank"
                        rel="noreferrer"
                        className="mt-1 inline-flex items-center gap-1 text-xs text-primary-700 hover:underline"
                      >
                        {t("calendar.viewPost")} <ExternalLink size={11} />
                      </a>
                    ) : null}
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}

/** Renders a UTC instant in the brand's timezone, which is the whole point. */
function formatInZone(iso: string, timeZone: string): string {
  try {
    return new Intl.DateTimeFormat(undefined, {
      dateStyle: "medium",
      timeStyle: "short",
      timeZone,
    }).format(new Date(iso));
  } catch {
    // An invalid stored timezone must not blank the whole calendar.
    return new Date(iso).toLocaleString();
  }
}
